//////////////////////////////////////////////////////////////////////////////
//
//  네트워크 매니저 (v3.0 - Interceptor 분리)
//
//  작성자: MyungHwan Kim
//  생성일: 2025-10-24
//  수정일: 2025-10-24 (v3.0 - Interceptor 분리 완료)
//
//  설명: 중앙화된 API 요청 관리자
//       - Axios 기반 HTTP 요청
//       - Token refresh 자동 처리 (Interceptor 분리)
//       - 에러 핸들링 및 사용자 알림
//       - 외부 서비스 (Mapbox 등) 지원
//
//  v3.0 개선 사항:
//       - Interceptor 로직 완전 분리 (140줄 감소)
//       - Token Refresh 로직 모듈화
//       - 코드 가독성 및 테스트 가능성 향상
//
//////////////////////////////////////////////////////////////////////////////

import axios, { AxiosResponse } from "axios"
import _ from "lodash"
import { ApiEndpoint, ApiRequestor, ApiResponse, ApiParams, FileUploadParams } from "./types/ApiTypes"
import { buildApiUrl } from "./utils/urlBuilder"
import { categorizeAxiosError, categorizeGenericError } from "./utils/errorClassifier"
import logger from "./utils/logger"
import { setupInterceptors } from "./interceptors"

// SUCCESS/ERROR 상수 정의
const SUCCESS = "SUCCESS"
const ERROR = "ERROR"

////////////////////////////////////////////////////////////////////////////////////
// API Configuration
////////////////////////////////////////////////////////////////////////////////////

/**
 * API 요청 설정
 */
const API_CONFIG = {
  timeout: 10000, // 10초 - 빠른 실패 감지
  maxRetries: 2, // 최대 2번 재시도 (총 3번 시도)
  retryDelay: 1000, // 첫 재시도 대기 시간 (ms)
}

/**
 * 재시도 가능한 HTTP 상태 코드
 */
const RETRYABLE_STATUS_CODES = [
  408, // Request Timeout
  429, // Too Many Requests
  500, // Internal Server Error
  502, // Bad Gateway
  503, // Service Unavailable
  504, // Gateway Timeout
]

/**
 * 재시도 가능한 네트워크 에러 코드
 */
const RETRYABLE_ERROR_CODES = [
  "ETIMEDOUT", // 타임아웃
  "ECONNABORTED", // 연결 중단
  "ENOTFOUND", // DNS 실패
  "ENETUNREACH", // 네트워크 도달 불가
  "ECONNREFUSED", // 연결 거부
  "ECONNRESET", // 연결 리셋
]

////////////////////////////////////////////////////////////////////////////////////
// API Registry
////////////////////////////////////////////////////////////////////////////////////

/**
 * API 그룹 저장소
 */
let API_REGISTRY: Record<string, Record<string, ApiEndpoint>> = {}

/**
 * Flattened API 맵 (성능 최적화) - 캐시됨
 */
let FLATTENED_APIS: Record<string, ApiEndpoint> = {}

/**
 * 등록된 모든 API를 flat한 맵으로 변환
 * 그룹 이름을 포함한 키 사용 (예: "User.pagedList", "Tenant.pagedList")
 */
const flattenApis = (): Record<string, ApiEndpoint> => {
  const flatApis: Record<string, ApiEndpoint> = {}
  _.forEach(API_REGISTRY, (_apis, _groupName) => {
    _.forEach(_apis, (endpoint, apiName) => {
      // 그룹 이름을 포함한 키 생성 (예: "User.pagedList")
      const fullKey = `${_groupName}.${apiName}`
      flatApis[fullKey] = endpoint
      // 기존 API 이름도 유지 (하위 호환성)
      if (!flatApis[apiName]) {
        flatApis[apiName] = endpoint
      }
    })
  })
  return flatApis
}

/**
 * API 그룹 등록
 */
export const registerApiGroup = (groupName: string, apis: Record<string, ApiEndpoint>) => {
  API_REGISTRY[groupName] = apis
  // 등록 시마다 캐시 갱신
  FLATTENED_APIS = flattenApis()
  logger.info(`API Group "${groupName}" registered`)
}

/**
 * 모든 API 등록 (초기화 시 호출)
 */
export const registerAllApis = (apiGroups: Record<string, Record<string, ApiEndpoint>>) => {
  console.log("🔍 [registerAllApis] 시작")
  console.log("🔍 [registerAllApis] apiGroups keys:", Object.keys(apiGroups))

  API_REGISTRY = apiGroups

  // 한 번만 flatten - 성능 개선
  FLATTENED_APIS = flattenApis()

  const registeredKeys = Object.keys(FLATTENED_APIS)
  console.log("🔍 [registerAllApis] FLATTENED_APIS keys:", registeredKeys)

  logger.info(`✅ ${Object.keys(apiGroups).length} API groups, ${registeredKeys.length} APIs registered`)
}

////////////////////////////////////////////////////////////////////////////////////
// Header 생성
////////////////////////////////////////////////////////////////////////////////////

const getHeader = async (api: ApiEndpoint): Promise<Record<string, string>> => {
  const headers: Record<string, string> = {}

  // Content-Type 설정
  if (api.contentType === "multipart/form-data") {
    headers["Content-Type"] = "multipart/form-data"
  } else {
    headers["Content-Type"] = "application/json"
  }

  // 참고: Authorization 헤더는 requestInterceptor에서 자동 추가됨

  return headers
}

////////////////////////////////////////////////////////////////////////////////////
// 파라미터 변환 (파일 업로드 등)
////////////////////////////////////////////////////////////////////////////////////

const arrangeParamz = (paramz: ApiParams | undefined, api: ApiEndpoint): FormData | ApiParams | undefined => {
  if (api.contentType === "multipart/form-data" && paramz) {
    const fileParams = paramz as FileUploadParams
    const newForm = new FormData()
    newForm.append("file", {
      uri: fileParams.uri,
      type: fileParams.type,
      name: fileParams.name,
    } as any)
    return newForm
  }

  return paramz
}

////////////////////////////////////////////////////////////////////////////////////
// Helper Functions
////////////////////////////////////////////////////////////////////////////////////

/**
 * 지연 함수 (재시도 대기용)
 */
const delay = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * 재시도 가능한 에러인지 판단
 */
const shouldRetry = (error: any): boolean => {
  // 네트워크 에러 코드 확인
  if (error.code && RETRYABLE_ERROR_CODES.includes(error.code)) {
    return true
  }

  // HTTP 상태 코드 확인
  if (axios.isAxiosError(error) && error.response && RETRYABLE_STATUS_CODES.includes(error.response.status)) {
    return true
  }

  return false
}

////////////////////////////////////////////////////////////////////////////////////
// API 호출
////////////////////////////////////////////////////////////////////////////////////

const callApi = async (name: string, paramz: (ApiParams & { pathParams?: Record<string, string> }) | undefined, api: ApiEndpoint): Promise<AxiosResponse> => {
  const startTime = Date.now()

  // 헤더 생성
  const headers = await getHeader(api)

  // URL 생성 (분리된 유틸리티 사용, pathParams 포함)
  const uri = buildApiUrl(api, paramz)

  // 외부 서비스의 경우 paramz를 undefined로 설정 (URL에 포함되므로)
  let requestParams = paramz
  if (api.externalService === "mapboxDirection" || api.externalService === "mapboxMaching") {
    requestParams = undefined
  }

  // 파라미터 변환
  const convertParamz = arrangeParamz(requestParams, api)

  // Request Config 생성
  const requestConfig: any = {
    url: uri,
    method: api.method,
    data: ["POST", "PUT", "PATCH"].includes(api.method) ? convertParamz : undefined,
    params: ["GET", "DELETE"].includes(api.method) ? convertParamz : undefined,
    timeout: api.timeout || API_CONFIG.timeout, // ✅ API별 타임아웃 또는 기본 10초
    headers: headers,
    responseType: api.responseType || "json",
  }

  // 로깅 (개발 환경에서만)
  logger.apiRequest(name, uri, convertParamz)

  return new Promise(function (resolve, reject) {
    axios(requestConfig)
      .then((response) => {
        const duration = Date.now() - startTime
        logger.apiResponse(name, response.status, duration)
        resolve(response)
      })
      .catch((error) => {
        // 에러 로깅 (분리된 Logger 사용)
        logger.apiError(name, error)

        // 원본 에러를 그대로 반환 (ReactAdmin에서 error.response.data.message를 읽을 수 있도록)
        // 재시도 로직에서만 에러 코드를 사용
        reject(error)
      })
  })
}

/**
 * 재시도 로직을 포함한 API 호출
 */
const callApiWithRetry = async (name: string, paramz: ApiParams | undefined, api: ApiEndpoint, noRetry: boolean = false): Promise<AxiosResponse> => {
  let lastError: any

  const maxRetries = noRetry ? 0 : API_CONFIG.maxRetries;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await callApi(name, paramz, api)

      // 성공 응답
      if (attempt > 0) {
        logger.info(`✅ [${name}] ${attempt + 1}번째 시도에서 성공`)
      }
      return response
    } catch (error) {
      lastError = error

      // 마지막 시도면 에러 던지기
      if (attempt === maxRetries) {
        logger.error(`❌ [${name}] 모든 재시도 실패 (${attempt + 1}번 시도)`)
        throw error
      }

      // 재시도 가능한 에러인지 확인
      if (shouldRetry(error)) {
        const delayMs = API_CONFIG.retryDelay * (attempt + 1) // Exponential Backoff: 1초, 2초
        logger.warn(`⏳ [${name}] 재시도 대기 중... (${attempt + 1}/${maxRetries + 1}, ${delayMs}ms)`)
        await delay(delayMs)
      } else {
        // 재시도 불가능한 에러 (400, 401, 404, 409 등)
        const status = (error as any)?.response?.status || "unknown"
        logger.warn(`⚠️ [${name}] 재시도 불가능한 에러 (상태: ${status})`)
        throw error
      }
    }
  }

  // 모든 시도 실패
  throw lastError
}

////////////////////////////////////////////////////////////////////////////////////
// Request Process
////////////////////////////////////////////////////////////////////////////////////

const requestProcess = (name: string, requestor: ApiRequestor = { paramz: {} }): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    // 캐시된 API 맵 사용 (성능 개선)
    const api = FLATTENED_APIS[name]

    if (!api) {
      const res = {
        name: name,
        status: 404,
        statusText: ERROR,
        message: "API가 없습니다.",
      }
      logger.error(`❌ API not found: "${name}"`)

      // 개발 모드에서만 사용 가능한 API 목록 출력
      const isDev = process.env.NODE_ENV === "development"
      if (isDev) {
        logger.error("🔍 Available APIs:", Object.keys(FLATTENED_APIS).slice(0, 20))
      }

      reject(res)
      return
    }

    const { paramz, pathParams } = requestor

    // pathParams를 포함한 파라미터 객체 생성
    const paramsWithPath = pathParams ? { ...paramz, pathParams } : paramz

    // ✅ 재시도 로직 포함된 API 호출 사용
    return callApiWithRetry(name, paramsWithPath, api, requestor.noRetry)
      .then((res: any) => {
        // 숫자 응답은 에러 코드이므로 에러로 처리
        if (typeof res === "number") {
          const error = new Error(`API Error: ${name} (Code: ${res})`)
            ; (error as any).name = name
            ; (error as any).statusText = ERROR
            ; (error as any).status = res
          reject(error)
          return
        }
        res.name = name
        res.statusText = SUCCESS
        resolve(res)
      })
      .catch((err: any) => {
        // err가 이미 객체인 경우에만 name 추가
        if (err && typeof err === "object") {
          err.name = name
          err.statusText = ERROR
        } else {
          // err가 숫자나 다른 타입인 경우 객체로 변환
          const error = new Error(err?.message || `API Error: ${name}`)
            ; (error as any).name = name
            ; (error as any).statusText = ERROR
            ; (error as any).status = typeof err === "number" ? err : undefined
          err = error
        }
        reject(err)
      })
  })
}

////////////////////////////////////////////////////////////////////////////////////
// Export: Main Request Function
////////////////////////////////////////////////////////////////////////////////////

/**
 * API 요청 함수
 * @param name - API 이름 (등록된 API key)
 * @param requestor - 요청 파라미터 (Store 또는 직접 파라미터)
 * @returns Promise<ApiResponse>
 */
export const request = (name: string, requestor: ApiRequestor = { paramz: {} }): Promise<ApiResponse> => {
  return requestProcess(name, requestor)
}

////////////////////////////////////////////////////////////////////////////////////
// Interceptor 초기화
////////////////////////////////////////////////////////////////////////////////////

// Interceptor 설정 (분리된 모듈 사용)
setupInterceptors(axios)

export default request
